import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Building2, ArrowRight } from "lucide-react";
import { usePageMeta } from "../../hooks/usePageMeta";
import FCFO3 from "../../assets/images/FCFO3.webp";
import officePartitioning1 from "../../assets/images/OfficePartitioning1.webp";
import spacePlanning1 from "../../assets/images/spacePlanning1.webp";

const OfficeFitOutSouthEast = () => {
  usePageMeta(
    "Office Fit-Out South East | Sandhurst Interiors",
    "Office fit-out specialists across the South East. Family-run with 30+ years experience. Full turnkey office fit-out, refurbishment and workspace upgrades in Hampshire, Berkshire and beyond."
  );

  return (
    <div className="min-h-screen">
      <section className="section-padding bg-primary-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Office Fit-Out Across the South East
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Full commercial office fit-out and workspace upgrades in Hampshire,
              Berkshire, Surrey and the wider South East—delivered by a
              family-run team with over 30 years of experience.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto space-y-6 text-lg text-gray-700"
          >
            <p>
              Sandhurst Interiors specialises in commercial office fit-out and
              refurbishment across the South East of England. From single offices
              to complete buildings, we deliver turnkey solutions that combine
              traditional craftsmanship with modern systems—partitioning,
              suspended ceilings, flooring, electrical and data, air
              conditioning and more.
            </p>
            <p>
              We work directly for end-user clients and with main contractors,
              in both the private and public sector. More than 70% of our
              projects come from returning clients, thanks to the relationships
              we build and the reliability we deliver on every job, regardless of
              size or budget.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            What an Office Fit-Out Includes
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              "Office partitioning (soundproof, glazed, steel, timber)",
              "Suspended ceilings and MF plasterboard ceilings",
              "Dry-lining, taping & jointing and plastering",
              "Interior & exterior decorating",
              "Flooring (carpet tiles, vinyl, specialist finishes)",
              "Air conditioning & ventilation (design & installation)",
              "Electrical & data installation and maintenance",
              "Space planning and full project management",
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg"
              >
                <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="flex items-center space-x-3 text-gray-700">
                <MapPin className="w-6 h-6 text-primary-600 flex-shrink-0" />
                <span className="font-medium">Serving the South East</span>
              </div>
              <p className="text-gray-700">
                We deliver office fit-out and refurbishment projects throughout
                London, Hampshire, Berkshire, Surrey and the wider South East.
                Our team provides dedicated site supervision, clear planning
                and coordination so your project runs smoothly with minimal
                disruption to your business.
              </p>
              <Link
                to="/contact#contact-form"
                onClick={() => sessionStorage.setItem("scrollToContactForm", "true")}
                className="btn-primary inline-flex items-center space-x-2"
              >
                <span>Get a Quote</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-lg bg-white">
                <img
                  src={FCFO3}
                  alt="Commercial office fit-out project in the South East"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-lg bg-white">
                <img
                  src={officePartitioning1}
                  alt="Office partitioning and fit-out"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="col-span-2 aspect-video rounded-lg overflow-hidden shadow-lg bg-white">
                <img
                  src={spacePlanning1}
                  alt="Office space planning and fit-out"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-primary-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to improve your workspace?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              Speak with our team today about your office fit-out across the
              South East.
            </p>
            <Link
              to="/contact#contact-form"
              onClick={() => sessionStorage.setItem("scrollToContactForm", "true")}
              className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-medium py-3 px-8 rounded-lg transition-all duration-200 inline-flex items-center space-x-2"
            >
              <Building2 className="w-4 h-4" />
              <span>Get in Touch</span>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default OfficeFitOutSouthEast;
